// RoomCard
import React, { useEffect, useMemo, useState } from "react";
import { fmt2, num } from "../../../helpers/rooms.helpers";
import WallCard from "./WallCard";
import CeilingLightPane from "./CeilingLightPane";

const CEILING_TYPES = [
    { value: "stretch", label: "Натяжной" },
    { value: "gkl", label: "ГКЛ" },
    { value: "paint", label: "Штукатурка/покраска" },
    { value: "armstrong", label: "Армстронг" },
    { value: "other", label: "Другое" },
];

export default function RoomCard({
    room,
    ri,
    roomComputed,
    open,
    dragOver,
    onToggle,
    onRemove,
    onMoveUp,
    onMoveDown,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDrop,
    actions,
    roomsAll,
}) {
    const walls = Array.isArray(room.walls) ? room.walls : [];
    const wallsCodes = walls.map((w) => w.code || "").filter(Boolean);
    const wallIds = useMemo(() => walls.map((w, wi) => String(w?.id || `${room.id || ri}:${wi}`)), [walls, room.id, ri]);

    const [openWallIds, setOpenWallIds] = useState([]);

    useEffect(() => {
        setOpenWallIds((prev) => prev.filter((id) => wallIds.includes(id)));
    }, [wallIds]);

    const isWallOpen = (wallId) => openWallIds.includes(wallId);

    const toggleWall = (wallId) => {
        setOpenWallIds((prev) => (prev.includes(wallId) ? prev.filter((x) => x !== wallId) : [...prev, wallId]));
    };

    const openAllWalls = () => setOpenWallIds(wallIds);
    const closeAllWalls = () => setOpenWallIds([]);

    const ceilingTypeLabel =
        CEILING_TYPES.find((x) => x.value === String(room?.ceilingType || "stretch"))?.label || "Натяжной";

    return (
        <div
            className={[
                "smeta-rooms__room",
                open ? "smeta-rooms__room--open" : "smeta-rooms__room--collapsed",
                dragOver ? "smeta-rooms__room--drag-over" : "",
            ].join(" ")}
            onDragOver={(e) => onDragOver(room.id, e)}
            onDrop={(e) => onDrop(room.id, e)}
        >
            <div className="smeta-rooms__room-head">
                <div className="smeta-rooms__room-head-left">
                    <button
                        type="button"
                        className="smeta-rooms__drag"
                        draggable
                        onDragStart={(e) => onDragStart(room.id, e)}
                        onDragEnd={onDragEnd}
                        title="Перетащить комнату"
                    />
                    <button type="button" className="smeta-rooms__room-toggle" onClick={onToggle}>
                        <div className="smeta-rooms__room-toggle-main">
                            <div className="smeta-rooms__room-toggle-title">
                                <span className="smeta-rooms__room-toggle-n">{ri + 1}.</span> {room.name || `Комната ${ri + 1}`}
                            </div>
                            <div className="smeta-rooms__room-toggle-sub">
                                <span>Пол: <b>{fmt2(roomComputed?.floorM2)}</b> м²</span>
                                <span>Потолок: <b>{fmt2(roomComputed?.ceilingM2)}</b> м²</span>
                                <span>{ceilingTypeLabel}{num(room?.ceilingDropMm) ? `, -${num(room.ceilingDropMm)} мм` : ""}</span>
                                <span>Свет: <b>{roomComputed?.roomTotals?.ceilingLightsQty || 0}</b></span>
                                <span>Стены нетто: <b>{fmt2(roomComputed?.roomTotals?.wallsNetM2)}</b> м²</span>
                                <span>Дверей: <b>{roomComputed?.roomTotals?.doorsQty || 0}</b></span>
                                <span>Окон: <b>{roomComputed?.roomTotals?.windowsQty || 0}</b></span>
                            </div>
                        </div>

                        <div className="smeta-rooms__room-toggle-right">
                            <div className="smeta-rooms__chips" aria-hidden="true">
                                {wallsCodes.map((c) => (
                                    <span key={c} className="smeta-rooms__chip">{c}</span>
                                ))}
                            </div>
                            <span className={`smeta-rooms__chev ${open ? "smeta-rooms__chev--open" : ""}`} aria-hidden="true" />
                        </div>
                    </button>
                </div>

                <div className="smeta-rooms__room-head-actions">
                    <button type="button" className="smeta-rooms__btn smeta-rooms__btn--small smeta-rooms__btn--neutral" onClick={onMoveUp} title="Вверх">
                        ↑
                    </button>
                    <button type="button" className="smeta-rooms__btn smeta-rooms__btn--small smeta-rooms__btn--neutral" onClick={onMoveDown} title="Вниз">
                        ↓
                    </button>
                    <button type="button" className="smeta-rooms__btn smeta-rooms__btn--danger smeta-rooms__btn--compact" onClick={onRemove}>
                        Удалить
                    </button>
                </div>
            </div>

            {open ? (
                <div className="smeta-rooms__room-body">
                    <div className="smeta-rooms__room-top">
                        <div className="smeta-rooms__room-title-row">
                            <input
                                className="smeta-rooms__input-title"
                                value={room.name || ""}
                                onChange={(e) => actions.updateRoom(ri, { name: e.target.value })}
                                placeholder="Название комнаты"
                            />
                        </div>

                        <div className="smeta-rooms__room-meta">
                            <div className="smeta-rooms__field">
                                <div className="smeta-rooms__label">Форма</div>
                                <select
                                    className="smeta-rooms__select"
                                    value={room.shape || "rectangle"}
                                    onChange={(e) => actions.setRoomShape(ri, e.target.value)}
                                >
                                    <option value="rectangle">Прямоугольная</option>
                                    <option value="polygon">Произвольная (N стен)</option>
                                </select>
                            </div>

                            <div className="smeta-rooms__field">
                                <div className="smeta-rooms__label">Высота стен, мм</div>
                                <input
                                    className="smeta-rooms__input"
                                    value={room.heightMm ?? ""}
                                    onChange={(e) => actions.updateRoom(ri, { heightMm: Number(e.target.value) || 0 })}
                                    inputMode="numeric"
                                />
                            </div>

                            <div className="smeta-rooms__field">
                                <div className="smeta-rooms__label">Тип пола</div>
                                <select
                                    className="smeta-rooms__select"
                                    value={room.floor || "lam"}
                                    onChange={(e) => actions.updateRoom(ri, { floor: e.target.value })}
                                >
                                    <option value="tile">Плитка/КГ</option>
                                    <option value="lam">Ламинат/КВ</option>
                                </select>
                            </div>

                            <div className="smeta-rooms__field">
                                <div className="smeta-rooms__label">Тёплый пол</div>
                                <label className="smeta-rooms__checkbox-line">
                                    <input
                                        type="checkbox"
                                        checked={!!room.warm}
                                        onChange={(e) => actions.updateRoom(ri, { warm: e.target.checked })}
                                    />
                                    В этой комнате
                                </label>
                            </div>

                            <div className="smeta-rooms__field">
                                <div className="smeta-rooms__label">Потолок (тип)</div>
                                <select
                                    className="smeta-rooms__select"
                                    value={String(room.ceilingType || "stretch")}
                                    onChange={(e) => actions.updateRoom(ri, { ceilingType: e.target.value })}
                                >
                                    {CEILING_TYPES.map((x) => (
                                        <option key={x.value} value={x.value}>
                                            {x.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="smeta-rooms__field">
                                <div className="smeta-rooms__label">Отступ потолка, мм</div>
                                <input
                                    className="smeta-rooms__input"
                                    value={room.ceilingDropMm ?? ""}
                                    onChange={(e) => actions.updateRoom(ri, { ceilingDropMm: num(e.target.value) })}
                                    inputMode="numeric"
                                    placeholder="Напр. 80"
                                />
                            </div>

                            <div className="smeta-rooms__field">
                                <div className="smeta-rooms__label">Потолок, м² (override)</div>
                                <input
                                    className="smeta-rooms__input"
                                    value={room.ceilingAreaM2Override ?? ""}
                                    onChange={(e) => actions.updateRoom(ri, { ceilingAreaM2Override: e.target.value })}
                                    inputMode="decimal"
                                    placeholder="Если пусто — как пол"
                                />
                            </div>

                            {room.shape === "rectangle" ? (
                                <>
                                    <div className="smeta-rooms__field">
                                        <div className="smeta-rooms__label">Длина, мм</div>
                                        <input
                                            className="smeta-rooms__input"
                                            value={room?.rect?.lengthMm ?? ""}
                                            onChange={(e) => actions.updateRoom(ri, { rect: { ...room.rect, lengthMm: Number(e.target.value) || 0 } })}
                                            inputMode="numeric"
                                        />
                                    </div>

                                    <div className="smeta-rooms__field">
                                        <div className="smeta-rooms__label">Ширина, мм</div>
                                        <input
                                            className="smeta-rooms__input"
                                            value={room?.rect?.widthMm ?? ""}
                                            onChange={(e) => actions.updateRoom(ri, { rect: { ...room.rect, widthMm: Number(e.target.value) || 0 } })}
                                            inputMode="numeric"
                                        />
                                    </div>

                                    <div className="smeta-rooms__field smeta-rooms__field--buttons">
                                        <button
                                            type="button"
                                            className="smeta-rooms__btn smeta-rooms__btn--neutral"
                                            onClick={() => actions.applyRectToWalls(ri)}
                                        >
                                            Применить L/W к 4 стенам
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="smeta-rooms__field">
                                        <div className="smeta-rooms__label">Площадь пола, м² (вручную)</div>
                                        <input
                                            className="smeta-rooms__input"
                                            value={room.floorAreaM2Override ?? ""}
                                            onChange={(e) => actions.updateRoom(ri, { floorAreaM2Override: Number(e.target.value) || 0 })}
                                            inputMode="decimal"
                                        />
                                    </div>

                                    <div className="smeta-rooms__field smeta-rooms__field--buttons">
                                        <button type="button" className="smeta-rooms__btn smeta-rooms__btn--neutral" onClick={() => actions.addWall(ri)}>
                                            + Добавить стену
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="smeta-rooms__room-totals">
                            <div className="smeta-rooms__total-item">
                                <span>Пол, м²:</span> <b>{fmt2(roomComputed?.floorM2)}</b>
                            </div>
                            <div className="smeta-rooms__total-item">
                                <span>Потолок, м²:</span> <b>{fmt2(roomComputed?.ceilingM2)}</b>
                            </div>
                            <div className="smeta-rooms__total-item">
                                <span>Свет, шт:</span> <b>{roomComputed?.roomTotals?.ceilingLightsQty || 0}</b>
                            </div>
                            <div className="smeta-rooms__total-item">
                                <span>Стены нетто, м²:</span> <b>{fmt2(roomComputed?.roomTotals?.wallsNetM2)}</b>
                            </div>
                            <div className="smeta-rooms__total-item">
                                <span>Дверей:</span> <b>{roomComputed?.roomTotals?.doorsQty || 0}</b>
                            </div>
                            <div className="smeta-rooms__total-item">
                                <span>Окон:</span> <b>{roomComputed?.roomTotals?.windowsQty || 0}</b>
                            </div>
                        </div>

                        <div className="smeta-rooms__grid-2">
                            <CeilingLightPane
                                title="Свет на потолке"
                                items={Array.isArray(room.ceilingLights) ? room.ceilingLights : []}
                                onAdd={() => actions.addCeilingLight(ri)}
                                onChange={(li, patch) => actions.updateCeilingLight(ri, li, patch)}
                                onRemove={(li) => actions.removeCeilingLight(ri, li)}
                            />
                        </div>

                        <div className="smeta-rooms__field-wide">
                            <div className="smeta-rooms__label">Заметки по комнате</div>
                            <textarea
                                className="smeta-rooms__textarea"
                                value={room.notes || ""}
                                onChange={(e) => actions.updateRoom(ri, { notes: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="smeta-rooms__walls">
                        <div className="smeta-rooms__walls-head">
                            <div className="smeta-rooms__sub-title">Стены</div>

                            <div className="smeta-rooms__walls-head-actions">
                                <button type="button" className="smeta-rooms__btn smeta-rooms__btn--small smeta-rooms__btn--neutral" onClick={openAllWalls}>
                                    Развернуть все
                                </button>
                                <button type="button" className="smeta-rooms__btn smeta-rooms__btn--small smeta-rooms__btn--neutral" onClick={closeAllWalls}>
                                    Свернуть все
                                </button>
                                {room.shape === "polygon" ? (
                                    <button type="button" className="smeta-rooms__btn smeta-rooms__btn--neutral" onClick={() => actions.addWall(ri)}>
                                        + Добавить стену
                                    </button>
                                ) : null}
                            </div>
                        </div>

                        {walls.map((wall, wi) => {
                            const cw = roomComputed?.walls?.[wi];
                            const wallId = String(wall?.id || `${room.id || ri}:${wi}`);

                            return (
                                <WallCard
                                    key={wallId}
                                    room={room}
                                    ri={ri}
                                    wall={wall}
                                    wi={wi}
                                    wallComputed={cw}
                                    open={isWallOpen(wallId)}
                                    onToggle={() => toggleWall(wallId)}
                                    allRooms={roomsAll}
                                    onAddDoorGlobal={actions.addDoor}
                                    onUpdateOpeningGlobal={actions.updateOpening}
                                    onRemoveOpeningGlobal={actions.removeOpening}
                                    onUpdateOpeningsBatch={actions.updateOpeningsBatch}
                                    onRemoveOpeningsBatch={actions.removeOpeningsBatch}
                                    onUpdateWall={(patch) => actions.updateWall(ri, wi, patch)}
                                    onRemoveWall={() => actions.removeWall(ri, wi)}
                                    onAddDoor={() => actions.addDoor(ri, wi)}
                                    onAddWindow={() => actions.addWindow(ri, wi)}
                                    onUpdateOpening={(kind, oi, patch) => actions.updateOpening(ri, wi, kind, oi, patch)}
                                    onRemoveOpening={(kind, oi) => actions.removeOpening(ri, wi, kind, oi)}
                                    onAddPoint={(kind) => actions.addPoint(ri, wi, kind)}
                                    onUpdatePoint={(kind, pi, patch) => actions.updatePoint(ri, wi, kind, pi, patch)}
                                    onRemovePoint={(kind, pi) => actions.removePoint(ri, wi, kind, pi)}
                                />
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
